import { normalizeCompanyDocumentName } from "@packages/shared-utils";

import type { TenantDocument } from "@/services/authService";

const CONTENT_TYPE_TO_EXT: Record<string, string> = {
  "application/pdf": ".pdf",
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/webp": ".webp",
  "text/plain": ".txt",
  "text/csv": ".csv",
  "application/csv": ".csv",
  "application/msword": ".doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
  "application/vnd.ms-excel": ".xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
  "application/vnd.oasis.opendocument.text": ".odt",
  "application/vnd.oasis.opendocument.spreadsheet": ".ods",
  "application/rtf": ".rtf",
  "text/rtf": ".rtf",
};

/**
 * Builds a safe local filename for save (normalized document name + original extension).
 * Blob URLs use an internal id segment; the browser only honors a friendly name when
 * saving from a same-origin blob URL after fetch.
 */
export function getCompanyDocumentDownloadFilename(
  doc: Pick<TenantDocument, "name" | "originalFilename" | "contentType">
): string {
  const extMatch = doc.originalFilename?.match(/\.([^.]+)$/i);
  const ext = extMatch
    ? `.${extMatch[1].toLowerCase()}`
    : CONTENT_TYPE_TO_EXT[doc.contentType] ?? ".pdf";
  const base = normalizeCompanyDocumentName(doc.name ?? "document");
  return `${base}${ext}`;
}

export async function downloadCompanyDocumentFile(doc: TenantDocument): Promise<void> {
  const filename = getCompanyDocumentDownloadFilename(doc);
  const response = await fetch(doc.fileUrl, { mode: "cors" });
  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
}
